import {
  getLinkedServicesUrl,
  addLinkedServiceUrl,
  updateLinkedServiceById,
  getLinkedServicesBackupUrl,
  reactivateLinkedServiceUrl,
} from "./constants";
import base from "../middlewares/interceptors";

export const getLinkedServices = async (sub_type?: any, system_type?: any) => {
  try {
    let response;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (sub_type) {
      queryParams.append("sub_type", sub_type);
    }
    if (system_type) {
      queryParams.append("system_id", system_type.toString());
    }

    // Construct the URL
    const queryString = queryParams.toString();
    const url = queryString
      ? `${getLinkedServicesUrl}?${queryString}`
      : getLinkedServicesUrl;

    response = await base.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getLinkedPaginatedServices = async (
  page: any,
  pSize: any,
  isActive: string = "true",
) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;
    let requestUrl = `${getLinkedServicesUrl}/all/pages?is_active=${isActive}&page=${pageN}&size=${pageS}`;
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const getAllLinkedPaginatedServices = async (
  page: any,
  pSize: any,
  searchTerm?: string,
  signal?: AbortSignal,
  sortBy?: string,
  sortOrder?: string,
  isActive?: string,
) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    // Build base URL with pagination
    let requestUrl = `${getLinkedServicesUrl}/all/pages?page=${pageN}&size=${pageS}`;

    // Add is_active parameter if provided (undefined means "All")
    if (isActive !== undefined) {
      requestUrl += `&is_active=${isActive}`;
    }

    // Add search parameter if searchTerm is provided and has minimum 3 characters
    if (searchTerm && searchTerm.trim().length >= 3) {
      requestUrl += `&name=${encodeURIComponent(searchTerm.trim())}`;
    }

    // If it is 'true' or undefined, use your backend default 'updated_on'
    const finalSortBy = (typeof sortBy === 'string' && sortBy !== 'true') ? sortBy : 'name';
    requestUrl += `&sort_by=${encodeURIComponent(finalSortBy)}`;

    const finalSortOrder =
      sortOrder?.toLowerCase() === "desc" ? "desc" : "asc";
    requestUrl += `&sort_order=${finalSortOrder}`;

    const response = await base.get(requestUrl, {
      signal, // Add abort signal for request cancellation
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getAllLinkedServices = async (linkedServiceIds: any) => {
  try {
    const linkedServiceIdsQueryParam = linkedServiceIds.join(",");

    const apiUrl = `${getLinkedServicesUrl}?linked_service_filter_ids=${linkedServiceIdsQueryParam}&is_active=true`;
    const response = await base.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getLinkedServiceDetail = async ({
  currentLinkedServiceId,
}: any) => {
  try {
    const response = await base.get(
      `${getLinkedServicesUrl}/${currentLinkedServiceId}/connection-keys-details`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const addLinkedService = async ({ payload }: any) => {
  try {
    const response = await base.post(addLinkedServiceUrl, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const updateLinkedService = async ({
  currentLinkedServiceId,
  payload,
}: any) => {
  try {
    // Extract comment from payload to use as query parameter
    const comment = payload?.comment?.trim();

    // Remove comment from the payload body
    const { comment: _, ...payloadWithoutComment } = payload;

    // Build request URL with comment as query parameter if present
    const requestUrl = comment
      ? `${updateLinkedServiceById}/${currentLinkedServiceId}?comment=${encodeURIComponent(comment)}`
      : `${updateLinkedServiceById}/${currentLinkedServiceId}`;

    const response = await base.put(requestUrl, payloadWithoutComment);

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const deleteLinkedService = async (currentLinkedServiceId: number) => {
  try {
    const response = await base.delete(
      `${updateLinkedServiceById}/${currentLinkedServiceId}`,
    );
    return { response: response.data, status: response.status };
  } catch (error) {
    console.error(error);
  }
};
export const getLinkedServicesBackup = async ({
  linkedServicesId,
  page,
  pSize,
}: any) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    let requestUrl;
    if (linkedServicesId && linkedServicesId !== 0) {
      requestUrl = `${getLinkedServicesBackupUrl}?linked_service_backup_filter_ids=${linkedServicesId}&page=${pageN}&size=${pageS}`;
    }
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Reactivate linked service
 * @param linkedServiceId - The ID of the linked service to reactivate
 * @returns Promise with reactivation result
 */
export const reactivateLinkedService = async (
  linkedServiceId: string | number,
) => {
  try {
    const response = await base.put(
      `${reactivateLinkedServiceUrl}/${linkedServiceId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error("Error reactivating linked service:", error);
    throw error;
  }
};
